import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAppSelector } from "../../../shared/store/hooks";
import { executeQuery } from "../api";
import { CUBE_NAME } from "../cubeSlice";
import {
  PeriodDashboard,
  CHART_COLORS,
  fmtVal,
  isExcludedMember,
} from "./PeriodDashboard";
import SaveQueryModal from "../../saved-queries/components/SaveQueryModal";
import { getSoldePeriodes } from "../../solde/api";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
} from "recharts";
import { applyDateMask, displayToIso, fixYear, isValidDate, isoToDisplay } from "../../../shared/lib/date";
import { fmtNum } from "../../../shared/lib/format";
import { thS } from "../../../shared/ui/styles";
import { FONTS, colors } from "../../../shared/ui/tokens";

// ── AVDASH brand palette ──────────────────────────────────────────────────────
const NW = {
  violet: colors.brand[600],
  violetDark: colors.brand[700],
  violetMed: colors.brand[100],
  violetLight: colors.brand[100],
  violetSoft: colors.neutral[100],
  rose: colors.accent[300],
  roseDark: colors.accent[500],
  roseMed: colors.accent[100],
  white: colors.neutral[0],
  gray50: colors.neutral[50],
  gray100: colors.neutral[100],
  gray200: colors.neutral[200],
  gray300: colors.neutral[300],
  gray500: colors.neutral[500],
  gray700: colors.neutral[700],
  gray800: colors.neutral[800],
  amber: colors.warning[500],
  amberLight: colors.warning[100],
  danger: colors.danger[500],
  dangerLight: colors.danger[50],
  success: colors.accent[500],
  teal: colors.accent[300],
  tealLight: colors.accent[100],
  orange: colors.warning[600],
  orangeLight: colors.warning[50],
};

const card = (extra?: React.CSSProperties): React.CSSProperties => ({
  background: NW.white,
  borderRadius: 14,
  border: `1.5px solid ${NW.gray200}`,
  boxShadow: "0 2px 16px rgba(29,91,168,0.07)",
  ...extra,
});

const tdS = (e?: React.CSSProperties): React.CSSProperties => ({
  border: `1px solid ${NW.gray200}`,
  padding: "8px 14px",
  whiteSpace: "nowrap",
  fontSize: 12,
  ...e,
});

// Use CHART_COLORS from PeriodDashboard for period accents
const PERIOD_COLORS = CHART_COLORS;

// ── Type Solde période ────────────────────────────────────────────────────────
interface SoldePeriode {
  code_periode: string;
  date_debut: string | null;
  date_fin: string | null;
  date_deb_priv: string | null;
  date_debut2: string | null;
  date_debut3: string | null;
  date_debut4: string | null;
  cod_dep: string | null;
}

interface Period {
  label: string;
  dateFrom: string;
  dateTo: string;
  // ▼ solde isolé par période ▼
  soldeCodePeriode?: string | null;
  // ▼ données complètes du solde pour les phases de démarque ▼
  soldeData?: SoldePeriode | null;
  // ▼ phase de démarque choisie (pour l'enregistrement en BD) ▼
  demarquePhase?: string | null;
}
type ViewMode = "table" | "dashboard";

// ── SoldeDropdown : sélecteur de solde ISOLÉ pour UNE seule période ───────────
// usedCodes = codes déjà choisis par LES AUTRES périodes (pas cette période)
// → ces codes sont masqués dans la liste déroulante
const SoldeDropdown: React.FC<{
  periodIndex: number;
  selected: string | null | undefined;
  usedCodes: string[];
  onSelect: (
    code: string,
    dateFrom: string,
    dateTo: string,
    data: SoldePeriode,
  ) => void;
  onClear: () => void;
  color: string;
}> = ({ periodIndex, selected, usedCodes, onSelect, onClear, color }) => {
  const [open, setOpen] = useState(false);
  const [periodes, setPeriodes] = useState<SoldePeriode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  // ── Position state — recalculé à chaque scroll/resize pendant l'ouverture ──
  const [dropPos, setDropPos] = useState<{ top: number; left: number } | null>(
    null,
  );

  const calcPos = useCallback(() => {
    const el = dropRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropH = 240;
    const top =
      spaceBelow >= dropH || spaceBelow >= spaceAbove
        ? rect.bottom + 6
        : Math.max(6, rect.top - dropH - 6);
    setDropPos({ top, left: rect.left });
  }, []);

  // Recalculer à l'ouverture
  useEffect(() => {
    if (open) calcPos();
  }, [open, calcPos]);

  // Recalculer pendant le scroll ET resize (tant qu'ouvert)
  useEffect(() => {
    if (!open) return;
    window.addEventListener("scroll", calcPos, true);
    window.addEventListener("resize", calcPos);
    return () => {
      window.removeEventListener("scroll", calcPos, true);
      window.removeEventListener("resize", calcPos);
    };
  }, [open, calcPos]);

  // Fermer si clic dehors — ne ferme QUE ce dropdown, pas les autres
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleOpen = async () => {
    setOpen((v) => !v);
    if (periodes.length > 0) return; // déjà chargé, on ne re-fetch pas
    setLoading(true);
    setError(null);
    try {
      const { data } = await getSoldePeriodes();
      // Filtrer les périodes avec des dates non-nulles
      const nonVides = data.filter(
        (p: SoldePeriode) => p.date_debut && p.date_fin,
      );
      setPeriodes(nonVides);
    } catch (err: any) {
      setError(err.message ?? "Erreur chargement périodes");
    } finally {
      setLoading(false);
    }
  };

  const fmtDate = (d: string | null) =>
    d ? new Date(d).toLocaleDateString("fr-FR") : "—";

  // Exclure les codes déjà pris par les autres périodes
  const disponibles = periodes.filter(
    (p) => !usedCodes.includes(p.code_periode),
  );

  return (
    <div
      ref={dropRef}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      <button
        onClick={handleOpen}
        title={`Solde P${periodIndex + 1} — indépendant des autres périodes`}
        style={{
          fontSize: 11,
          padding: "4px 10px",
          borderRadius: 99,
          cursor: "pointer",
          border: `1.5px solid ${selected ? "#00A08C" : `${color}44`}`,
          background: selected ? "#D3EFEA" : `${color}10`,
          color: selected ? "#006357" : color,
          fontWeight: selected ? 700 : 600,
          transition: "all 0.15s",
          display: "flex",
          alignItems: "center",
          gap: 4,
          whiteSpace: "nowrap",
          fontFamily: FONTS.body,
        }}
      >
        <span style={{ fontSize: 12 }}>🏷️</span>
        {selected ? selected : "Solde"}
        <span style={{ fontSize: 9, opacity: 0.6 }}>▾</span>
      </button>

      {/* Bouton × effacer — visible uniquement si une période est sélectionnée */}
      {selected && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          title={`Effacer le solde de P${periodIndex + 1}`}
          style={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            background: "#D64545",
            color: "#FFFFFF",
            border: "none",
            cursor: "pointer",
            fontSize: 11,
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          ×
        </button>
      )}

      {open && dropPos && (
        <div
          style={{
            position: "fixed",
            top: dropPos.top,
            left: dropPos.left,
            background: NW.white,
            borderRadius: 12,
            boxShadow: "0 8px 30px rgba(24,75,140,0.15)",
            minWidth: 300,
            maxHeight: 240,
            overflowY: "auto",
            zIndex: 9999,
            border: "1.5px solid #A9DFD7",
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: "9px 14px",
              background: "linear-gradient(135deg, #006357, #00A08C)",
              borderRadius: "10px 10px 0 0",
              display: "flex",
              alignItems: "center",
              gap: 7,
            }}
          >
            <span style={{ fontSize: 13 }}>🏷️</span>
            <span
              style={{
                fontFamily: FONTS.heading,
                fontWeight: 700,
                fontSize: 11,
                color: "#FFFFFF",
              }}
            >
              Solde — P{periodIndex + 1}
            </span>
            <span
              style={{
                fontSize: 10,
                color: "rgba(255,255,255,0.6)",
                marginLeft: 4,
              }}
            >
              (dates auto · isolé)
            </span>
          </div>

          {/* Contenu */}
          {loading ? (
            <div
              style={{
                padding: 16,
                textAlign: "center",
                color: NW.gray500,
                fontSize: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <span className="spinner" style={{ width: 14, height: 14 }} />
              Chargement…
            </div>
          ) : error ? (
            <div
              style={{
                padding: 12,
                color: NW.danger,
                fontSize: 12,
                background: NW.dangerLight,
                margin: 8,
                borderRadius: 8,
              }}
            >
              ⚠ {error}
            </div>
          ) : disponibles.length === 0 ? (
            <div
              style={{
                padding: 16,
                textAlign: "center",
                color: NW.gray500,
                fontSize: 12,
                fontStyle: "italic",
              }}
            >
              {periodes.length === 0
                ? "Aucune période disponible"
                : "Toutes les périodes sont déjà utilisées"}
            </div>
          ) : (
            disponibles.map((p) => {
              const isActive = selected === p.code_periode;
              return (
                <div
                  key={p.code_periode}
                  onClick={() => {
                    const from = p.date_debut ? p.date_debut.slice(0, 10) : "";
                    const to = p.date_fin ? p.date_fin.slice(0, 10) : "";
                    onSelect(p.code_periode, from, to, p);
                    setOpen(false);
                  }}
                  style={{
                    padding: "9px 14px",
                    cursor: "pointer",
                    background: isActive ? "#D3EFEA" : NW.white,
                    borderBottom: `1px solid ${NW.gray100}`,
                    transition: "background 0.1s",
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                  }}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as HTMLElement).style.background =
                      isActive ? "#A9DFD7" : "#F8FAFC")
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as HTMLElement).style.background =
                      isActive ? "#D3EFEA" : NW.white)
                  }
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 7 }}
                  >
                    {isActive && (
                      <span
                        style={{
                          color: "#00A08C",
                          fontWeight: 800,
                          fontSize: 12,
                          flexShrink: 0,
                        }}
                      >
                        ✓
                      </span>
                    )}
                    <span
                      style={{
                        fontWeight: 700,
                        fontSize: 12,
                        color: isActive ? "#006357" : NW.gray800,
                        fontFamily: FONTS.heading,
                      }}
                    >
                      {p.code_periode}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: NW.gray500,
                      paddingLeft: isActive ? 19 : 0,
                    }}
                  >
                    📅 {fmtDate(p.date_debut)} → {fmtDate(p.date_fin)}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// ── DemarqueDropdown : bouton de démarque isolé par période ──────────────────
const DemarqueDropdown: React.FC<{
  soldeData: SoldePeriode | null | undefined;
  selectedPhase?: string | null;
  onSelect: (dateFrom: string, dateTo: string, phaseLabel: string) => void;
  color: string;
}> = ({ soldeData, selectedPhase, onSelect, color }) => {
  const [open, setOpen] = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const phases: {
    label: string;
    icon: string;
    dateFrom: string;
    dateTo: string;
  }[] = [];
  if (soldeData) {
    const fin = soldeData.date_fin ? soldeData.date_fin.slice(0, 10) : "";
    // Parse manuellement pour éviter le décalage UTC/local (même logique que QueryBuilder)
    const dayBefore = (d: string) => {
      const [year, month, day] = d.slice(0, 10).split("-").map(Number);
      const dt = new Date(year, month - 1, day);
      dt.setDate(dt.getDate() - 1);
      const yy = dt.getFullYear();
      const mm = String(dt.getMonth() + 1).padStart(2, "0");
      const dd = String(dt.getDate()).padStart(2, "0");
      return `${yy}-${mm}-${dd}`;
    };
    // Démarque 1 : date_debut → date_debut2 - 1
    if (soldeData.date_debut)
      phases.push({
        label: "Démarque 1",
        icon: "🏷️",
        dateFrom: soldeData.date_debut.slice(0, 10),
        dateTo: soldeData.date_debut2 ? dayBefore(soldeData.date_debut2) : fin,
      });
    // Démarque 2 : date_debut2 → date_debut3 - 1
    if (soldeData.date_debut2)
      phases.push({
        label: "Démarque 2",
        icon: "🏷️",
        dateFrom: soldeData.date_debut2.slice(0, 10),
        dateTo: soldeData.date_debut3 ? dayBefore(soldeData.date_debut3) : fin,
      });
    // Démarque 3 : date_debut3 → date_debut4 - 1
    if (soldeData.date_debut3)
      phases.push({
        label: "Démarque 3",
        icon: "🏷️",
        dateFrom: soldeData.date_debut3.slice(0, 10),
        dateTo: soldeData.date_debut4 ? dayBefore(soldeData.date_debut4) : fin,
      });
    // Démarque 4 : date_debut4 → date_fin
    if (soldeData.date_debut4)
      phases.push({
        label: "Démarque 4",
        icon: "🏷️",
        dateFrom: soldeData.date_debut4.slice(0, 10),
        dateTo: fin,
      });
  }

  const isActive = !!soldeData;

  return (
    <div
      ref={dropRef}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      <button
        onClick={() => isActive && setOpen((v) => !v)}
        disabled={!isActive}
        title={
          isActive
            ? "Choisir une phase de démarque"
            : "Sélectionnez d'abord un solde"
        }
        style={{
          fontSize: 11,
          padding: "4px 10px",
          borderRadius: 99,
          cursor: isActive ? "pointer" : "not-allowed",
          border: `1.5px solid ${open ? "#184B8C" : isActive ? `${color}55` : "#E2E8F0"}`,
          background: open ? "#EEF4FB" : isActive ? `${color}10` : "#F1F5F9",
          color: isActive ? "#184B8C" : "#64748B",
          fontWeight: isActive ? 700 : 500,
          opacity: isActive ? 1 : 0.55,
          transition: "all 0.15s",
          display: "flex",
          alignItems: "center",
          gap: 4,
          whiteSpace: "nowrap",
          fontFamily: FONTS.body,
          boxShadow: open ? "0 0 0 2px #D7E5F5" : "none",
        }}
      >
        <span style={{ fontSize: 12 }}>📉</span>
        {selectedPhase ? selectedPhase : "Démarque"}
        <span style={{ fontSize: 9, opacity: 0.7 }}>▾</span>
      </button>

      {open && isActive && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            background: NW.white,
            borderRadius: 12,
            boxShadow: "0 8px 30px rgba(24,75,140,0.18)",
            minWidth: 270,
            zIndex: 9999,
            border: "1.5px solid #D7E5F5",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: "9px 14px",
              background: "linear-gradient(135deg, #184B8C, #184B8C)",
              display: "flex",
              alignItems: "center",
              gap: 7,
            }}
          >
            <span style={{ fontSize: 13 }}>📉</span>
            <div>
              <div
                style={{
                  fontFamily: FONTS.heading,
                  fontWeight: 700,
                  fontSize: 11,
                  color: "#FFFFFF",
                }}
              >
                Phases de démarque
              </div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.6)" }}>
                {soldeData!.code_periode} — dates auto-remplies
              </div>
            </div>
          </div>

          {/* Phases */}
          {phases.length === 0 ? (
            <div
              style={{
                padding: 16,
                textAlign: "center",
                color: NW.gray500,
                fontSize: 12,
                fontStyle: "italic",
              }}
            >
              Aucune phase de démarque disponible
            </div>
          ) : (
            phases.map((ph, idx) => {
              const isSelected = selectedPhase === ph.label;
              return (
                <div
                  key={idx}
                  onClick={() => {
                    onSelect(ph.dateFrom, ph.dateTo, ph.label);
                    setOpen(false);
                  }}
                  style={{
                    padding: "10px 14px",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    borderBottom:
                      idx < phases.length - 1
                        ? `1px solid ${NW.gray100}`
                        : "none",
                    transition: "background 0.1s",
                    background: isSelected ? NW.violetSoft : NW.white,
                  }}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as HTMLElement).style.background =
                      NW.violetSoft)
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as HTMLElement).style.background =
                      isSelected ? NW.violetSoft : NW.white)
                  }
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <span style={{ fontSize: 13 }}>{ph.icon}</span>
                    <span
                      style={{
                        fontWeight: 700,
                        fontSize: 12,
                        color: "#184B8C",
                        fontFamily: FONTS.heading,
                      }}
                    >
                      {ph.label}
                    </span>
                    {isSelected && (
                      <span
                        style={{
                          color: "#00A08C",
                          fontWeight: 800,
                          fontSize: 11,
                        }}
                      >
                        ✓
                      </span>
                    )}
                  </div>
                  <div
                    style={{ fontSize: 11, color: NW.gray500, paddingLeft: 19 }}
                  >
                    📅 {new Date(ph.dateFrom).toLocaleDateString("fr-FR")} →{" "}
                    {ph.dateTo
                      ? new Date(ph.dateTo).toLocaleDateString("fr-FR")
                      : "—"}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// ── Period row editor ─────────────────────────────────────────────────────────
// ── DateMaskField : champ JJ/MM/AAAA + icône 📅 (calendrier natif caché) ──────
// Même comportement/apparence que le filtre "Période" en haut de page.
const DateMaskField: React.FC<{
  value: string; // ISO yyyy-mm-dd
  onChangeIso: (iso: string) => void;
  min?: string;
  max?: string;
  color: string;
}> = ({ value, onChangeIso, min, max, color }) => {
  const [display, setDisplay] = useState(isoToDisplay(value));
  const [hasError, setHasError] = useState(false);
  const textRef = useRef<HTMLInputElement>(null);
  const pickerRef = useRef<HTMLInputElement>(null);

  // Resynchroniser l'affichage si la valeur ISO change depuis l'extérieur
  useEffect(() => {
    setDisplay(isoToDisplay(value));
  }, [value]);

  const triggerShake = () => {
    setHasError(true);
    setTimeout(() => setHasError(false), 600);
  };

  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      <input
        ref={textRef}
        type="text"
        inputMode="numeric"
        placeholder="JJ/MM/AAAA"
        maxLength={10}
        value={display}
        onFocus={() => pickerRef.current?.blur()}
        onChange={(e) => {
          const masked = applyDateMask(e.target.value);
          setDisplay(masked);
          if (hasError) setHasError(false);
          if (masked.length === 10) {
            const iso = fixYear(displayToIso(masked));
            if (
              isValidDate(iso) &&
              (!min || iso >= min) &&
              (!max || iso <= max)
            ) {
              onChangeIso(iso);
            }
          }
        }}
        onBlur={() => {
          if (display.length === 10) {
            const iso = fixYear(displayToIso(display));
            if (
              isValidDate(iso) &&
              (!min || iso >= min) &&
              (!max || iso <= max)
            ) {
              onChangeIso(iso);
              setDisplay(isoToDisplay(iso));
            } else {
              triggerShake();
              onChangeIso("");
              setDisplay("");
            }
          } else if (display === "") {
            onChangeIso("");
          } else {
            triggerShake();
            setDisplay(isoToDisplay(value));
          }
        }}
        style={{
          border: `1.5px solid ${hasError ? NW.danger : value ? NW.violet : NW.gray200}`,
          borderRadius: 9,
          padding: "5px 26px 5px 9px",
          fontSize: 12,
          fontFamily: FONTS.body,
          color: NW.gray800,
          outline: "none",
          width: 110,
          letterSpacing: 0.5,
          boxShadow: hasError ? `0 0 0 2px ${NW.danger}33` : undefined,
        }}
      />
      {/* 📅 Icône — ouvre le calendrier natif */}
      <button
        type="button"
        onClick={() => {
          const el = pickerRef.current;
          if (!el) return;
          if (typeof (el as any).showPicker === "function")
            (el as any).showPicker();
          else el.focus();
        }}
        title="Ouvrir le calendrier"
        style={{
          position: "absolute",
          right: 4,
          top: "50%",
          transform: "translateY(-50%)",
          background: "none",
          border: "none",
          cursor: "pointer",
          fontSize: 13,
          padding: 2,
          lineHeight: 1,
          color,
        }}
      >
        📅
      </button>
      {/* Input date natif caché — pilote le calendrier */}
      <input
        ref={pickerRef}
        type="date"
        value={value || ""}
        min={min}
        max={max}
        onChange={(e) => {
          const iso = e.target.value;
          if (!iso) return;
          onChangeIso(iso);
          setDisplay(isoToDisplay(iso));
        }}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          opacity: 0,
          pointerEvents: "none",
          border: "none",
        }}
        tabIndex={-1}
      />
    </div>
  );
};

const PeriodRow: React.FC<{
  index: number;
  period: Period;
  usedSoldeCodes: string[];
  onChange: (p: Period) => void;
  onRemove: () => void;
}> = ({ index, period, usedSoldeCodes, onChange, onRemove }) => {
  const color = PERIOD_COLORS[index % PERIOD_COLORS.length];
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
        padding: "10px 14px",
        background: `${color}0A`,
        border: `1.5px solid ${color}33`,
        borderRadius: 10,
        marginBottom: 8,
      }}
    >
      {/* Period badge */}
      <div
        style={{
          background: color,
          color: "#FFFFFF",
          borderRadius: 8,
          padding: "4px 12px",
          fontSize: 12,
          fontWeight: 700,
          fontFamily: FONTS.heading,
          flexShrink: 0,
        }}
      >
        P{index + 1}
      </div>

      {/* Label input */}
      <input
        type="text"
        value={period.label}
        placeholder={`Période ${index + 1}`}
        onChange={(e) => onChange({ ...period, label: e.target.value })}
        style={{
          border: `1.5px solid ${color}44`,
          borderRadius: 9,
          padding: "5px 10px",
          fontSize: 12,
          fontFamily: FONTS.body,
          color: NW.gray800,
          outline: "none",
          background: NW.white,
          width: 130,
        }}
      />

      {/* Date from */}
      <label
        style={{
          fontSize: 11,
          color: NW.gray500,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 1,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        De
        <DateMaskField
          value={period.dateFrom}
          onChangeIso={(iso) => onChange({ ...period, dateFrom: iso })}
          max={period.dateTo || undefined}
          color={color}
        />
      </label>

      <span style={{ color: NW.gray300, fontSize: 16, fontWeight: 300 }}>
        →
      </span>

      {/* Date to */}
      <label
        style={{
          fontSize: 11,
          color: NW.gray500,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 1,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        À
        <DateMaskField
          value={period.dateTo}
          onChangeIso={(iso) => onChange({ ...period, dateTo: iso })}
          min={period.dateFrom || undefined}
          color={color}
        />
      </label>

      {/* Bouton Solde ISOLÉ par période
          usedSoldeCodes = codes choisis par les AUTRES périodes → filtrés */}
      <SoldeDropdown
        periodIndex={index}
        selected={period.soldeCodePeriode}
        usedCodes={usedSoldeCodes}
        color={color}
        onSelect={(code, from, to, data) =>
          onChange({
            ...period,
            soldeCodePeriode: code,
            soldeData: data,
            dateFrom: from,
            dateTo: to,
            demarquePhase: null, // reset démarque quand on change de solde
          })
        }
        onClear={() =>
          onChange({
            ...period,
            soldeCodePeriode: null,
            soldeData: null,
            demarquePhase: null,
          })
        }
      />

      {/* Bouton Démarque — actif uniquement si un solde est choisi
          Affiche le label de la phase choisie et la mémorise dans demarquePhase */}
      <DemarqueDropdown
        soldeData={period.soldeData}
        selectedPhase={period.demarquePhase}
        color={color}
        onSelect={(from, to, phaseLabel) =>
          onChange({
            ...period,
            dateFrom: from,
            dateTo: to,
            demarquePhase: phaseLabel,
          })
        }
      />

      {/* Remove button */}
      {index > 1 && (
        <button
          onClick={onRemove}
          title="Supprimer cette période"
          style={{
            background: NW.dangerLight,
            border: `1px solid ${NW.danger}30`,
            color: NW.danger,
            cursor: "pointer",
            fontWeight: 800,
            fontSize: 16,
            borderRadius: 8,
            width: 30,
            height: 30,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          ×
        </button>
      )}
    </div>
  );
};

interface ComparisonViewProps {
  defaultMeasure?: string;
  defaultMeasures?: string[]; // ✅ NOUVEAU : permet de passer plusieurs mesures

  defaultDimension?: string;
  defaultHierarchy?: string;
  defaultLevel?: string;
  initialComparisonPeriods?: Array<{
    label?: string;
    dateFrom: string;
    dateTo: string;
    soldeCodePeriode?: string | null;
    demarquePhase?: string | null;
  }> | null;
  // Incrémenté depuis QueryBuilder pour déclencher un auto-run après chargement
  autoRunToken?: number;
  // ── Type à passer à SaveQueryModal (ex: "magazin", "mdx") ────────────────
  saveType?: string;
  // ── Top N passé depuis la view parente (prioritaire sur Redux) ────────────
  overrideTopN?: number | null;
}

const ComparisonView: React.FC<ComparisonViewProps> = ({
  defaultMeasure,
  defaultMeasures,
  defaultDimension,
  defaultHierarchy,
  defaultLevel,
  initialComparisonPeriods,
  autoRunToken = 0,
  saveType = "mdx",
  overrideTopN,
}) => {
  const {
    measures: reduxMeasures,
    rows: reduxRows,
    columns: reduxColumns,
    topN: reduxTopN,
  } = useAppSelector((s) => s.query);

  // ── Top N : priorité à overrideTopN (view locale), sinon Redux ───────────
  const resolvedTopN = overrideTopN !== undefined ? overrideTopN : reduxTopN;
  const effectiveTopN: number =
    resolvedTopN !== null && resolvedTopN > 0 ? resolvedTopN : Infinity;

  const selMeasures =
    defaultMeasures && defaultMeasures.length > 0
      ? defaultMeasures
      : defaultMeasure
        ? [defaultMeasure]
        : reduxMeasures;

  const rows = defaultDimension
    ? [
        {
          dimensionName: defaultDimension,
          hierarchyName: defaultHierarchy ?? defaultDimension,
          levelName: defaultLevel ?? defaultDimension,
        },
      ]
    : reduxRows;
  const columns = defaultDimension ? [] : reduxColumns;

  // ── Initialisation des périodes ───────────────────────────────────────────
  const buildInitialPeriods = (saved?: Array<any> | null): Period[] => {
    if (saved && saved.length > 0) {
      return saved.map((p, i) => ({
        label: p.label ?? `Période ${i + 1}`,
        dateFrom: p.dateFrom ?? p.DateFrom ?? "",
        dateTo: p.dateTo ?? p.DateTo ?? "",
        soldeCodePeriode: p.soldeCodePeriode ?? null,
        soldeData: null,
        demarquePhase: p.demarquePhase ?? null,
      }));
    }
    return [
      {
        label: "Période 1",
        dateFrom: "",
        dateTo: "",
        soldeCodePeriode: null,
        soldeData: null,
        demarquePhase: null,
      },
      {
        label: "Période 2",
        dateFrom: "",
        dateTo: "",
        soldeCodePeriode: null,
        soldeData: null,
        demarquePhase: null,
      },
    ];
  };

  const [periods, setPeriods] = useState<Period[]>(() =>
    buildInitialPeriods(initialComparisonPeriods),
  );
  const [results, setResults] = useState<{ period: Period; data: any }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [showSaveModal, setShowSaveModal] = useState(false);
  // Chart type per column member: "bar" | "line" | "area"
  const [compChartTypes, setCompChartTypes] = useState<Record<string, string>>(
    {},
  );

  // ── Auto-run quand initialComparisonPeriods change ────────────────────────
  const initialPeriodsKey = JSON.stringify(initialComparisonPeriods ?? null);
  useEffect(() => {
    if (initialComparisonPeriods && initialComparisonPeriods.length > 0) {
      setPeriods(buildInitialPeriods(initialComparisonPeriods));
      setResults([]);
      setError(null);
    }
  }, [initialPeriodsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-run déclenché depuis QueryBuilder via le token ──────────────────
  const isFirstAutoRun = useRef(true);
  useEffect(() => {
    if (isFirstAutoRun.current) {
      isFirstAutoRun.current = false;
      return;
    }
    if (autoRunToken > 0) {
      const t = setTimeout(() => {
        runComparison();
      }, 80);
      return () => clearTimeout(t);
    }
  }, [autoRunToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const addPeriod = () =>
    setPeriods((p) => [
      ...p,
      {
        label: `Période ${p.length + 1}`,
        dateFrom: "",
        dateTo: "",
        soldeCodePeriode: null,
        soldeData: null,
        demarquePhase: null,
      },
    ]);
  const updatePeriod = (i: number, p: Period) =>
    setPeriods((prev) => prev.map((x, idx) => (idx === i ? p : x)));
  const removePeriod = (i: number) =>
    setPeriods((p) => p.filter((_, idx) => idx !== i));

  const runComparison = useCallback(async () => {
    if (selMeasures.length === 0) {
      setError("Sélectionnez au moins une mesure.");
      return;
    }
    if (rows.length === 0 && columns.length === 0) {
      setError("Sélectionnez au moins une dimension en lignes ou colonnes.");
      return;
    }
    const valid = periods.filter(
      (p) =>
        p.dateFrom &&
        p.dateTo &&
        (p.dateFrom <= p.dateTo || !!p.demarquePhase || !!p.soldeCodePeriode),
    );
    if (valid.length < 1) {
      setError("Définissez au moins une période complète.");
      return;
    }

    setLoading(true);
    setError(null);
    setResults([]);
    try {
      const res = await Promise.all(
        valid.map((p) =>
          executeQuery({
            cubeName: CUBE_NAME,
            measures: selMeasures,
            rows,
            columns,
            dateFrom: p.dateFrom,
            dateTo: p.dateTo,
            ...(p.soldeCodePeriode
              ? { soldeCodePeriode: p.soldeCodePeriode }
              : {}),
          } as any).then((r) => ({ period: p, data: r.data })),
        ),
      );
      setResults(res);
    } catch (e: any) {
      setError(e.message || "Erreur lors de la comparaison");
    } finally {
      setLoading(false);
    }
  }, [selMeasures, rows, columns, periods]);

  // ── Table view ────────────────────────────────────────────────────────────
  const renderTable = () => {
    if (results.length === 0) return null;
    const first = results[0].data;
    if (!first?.columns?.length) {
      return (
        <div
          style={{
            color: NW.gray500,
            fontSize: 13,
            padding: 20,
            textAlign: "center",
            fontFamily: FONTS.body,
          }}
        >
          Aucun résultat.
        </div>
      );
    }

    const rowDimCount =
      rows.length > 0 ? Math.min(rows.length, first.columns.length) : 0;
    const rowHeaders: string[] = first.columns.slice(0, rowDimCount);
    const dataCols: string[] = first.columns.slice(rowDimCount);
    const filteredResults = results.map((r) => ({
      ...r,
      data: {
        ...r.data,
        rows: (r.data.rows ?? []).filter((row: any[]) => {
          // Exclure les membres OLAP all/unknown/inconnue/vide
          for (let di = 0; di < Math.max(rowDimCount, 1); di++) {
            if (isExcludedMember(row[di])) return false;
          }
          // Exclure les lignes dont toutes les valeurs de mesures sont null/0
          const hasData = row.slice(rowDimCount).some((val) => {
            if (val == null || val === "") return false;
            const n = parseFloat(String(val));
            return !isNaN(n) && n !== 0;
          });
          return hasData;
        }),
      },
    }));
    const filteredFirst = filteredResults[0].data;

    // ✅ Tri desc par 1ère colonne de données + application du Top N
    const allFirstRows: any[][] = filteredFirst.rows;
    const sortedTopNIndices: number[] = allFirstRows
      .map((_: any, idx: number) => idx)
      .sort((a: number, b: number) => {
        const va =
          Math.abs(parseFloat(String(allFirstRows[a]?.[rowDimCount] ?? 0))) ||
          0;
        const vb =
          Math.abs(parseFloat(String(allFirstRows[b]?.[rowDimCount] ?? 0))) ||
          0;
        return vb - va;
      })
      .slice(0, effectiveTopN);
    const topNFirstRows: any[][] = sortedTopNIndices.map(
      (idx: number) => allFirstRows[idx],
    );

    return (
      <div style={card({ overflow: "hidden" })}>
        {/* Badge Top N affiché au-dessus du tableau */}
        <div
          style={{
            padding: "6px 14px",
            background: NW.violetSoft,
            borderBottom: `1.5px solid ${NW.violetMed}`,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: NW.violetDark,
              fontFamily: FONTS.heading,
            }}
          >
            🏆 Top {isFinite(effectiveTopN) ? effectiveTopN : "Tout"} sur{" "}
            {allFirstRows.length} lignes
          </span>
        </div>
        <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: 560 }}>
          <table
            style={{
              borderCollapse: "collapse",
              fontSize: 12,
              fontFamily: FONTS.body,
              minWidth: 600,
              width: "100%",
            }}
          >
            <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
              <tr>
                {rowHeaders.map((h, i) => (
                  <th
                    key={i}
                    rowSpan={2}
                    style={thS({
                      background: NW.violetDark,
                      color: "#FFFFFF",
                      textAlign: "left",
                      verticalAlign: "middle",
                      fontFamily: FONTS.heading,
                      fontSize: 12,
                      borderRight: `3px solid ${NW.violet}`,
                      position: "sticky",
                      left: 0,
                      zIndex: 20,
                      boxShadow: "2px 0 6px rgba(15,23,42,0.08)",
                      minWidth: 160,
                    })}
                  >
                    {h}
                  </th>
                ))}
                {dataCols.map((col, ci) => (
                  <th
                    key={ci}
                    colSpan={results.length}
                    style={thS({
                      background: NW.rose,
                      color: "#FFFFFF",
                      fontFamily: FONTS.heading,
                      fontSize: 11,
                      borderBottom: `2px solid ${NW.roseDark}`,
                    })}
                  >
                    {col}
                  </th>
                ))}
              </tr>
              <tr>
                {dataCols.flatMap((_, ci) =>
                  results.map((r, ri) => {
                    const periodColor =
                      PERIOD_COLORS[ri % PERIOD_COLORS.length];
                    return (
                      <th
                        key={`${ci}-${ri}`}
                        style={thS({
                          background: periodColor,
                          color: "#FFFFFF",
                          fontSize: 11,
                          fontFamily: FONTS.heading,
                        })}
                      >
                        {r.period.label}
                        {r.period.soldeCodePeriode && (
                          <div
                            style={{
                              fontSize: 9,
                              fontWeight: 700,
                              opacity: 0.95,
                              marginTop: 2,
                              background: "rgba(255,255,255,0.22)",
                              borderRadius: 4,
                              padding: "1px 5px",
                              display: "inline-block",
                            }}
                          >
                            🏷️ {r.period.soldeCodePeriode}
                          </div>
                        )}
                        <div
                          style={{
                            fontSize: 9,
                            fontWeight: 400,
                            opacity: 0.85,
                            marginTop: 2,
                          }}
                        >
                          {r.period.dateFrom} → {r.period.dateTo}
                        </div>
                      </th>
                    );
                  }),
                )}
              </tr>
            </thead>
            <tbody>
              {topNFirstRows.map((row: any[], topIdx: number) => {
                const origIdx = sortedTopNIndices[topIdx];
                const rowBg = topIdx % 2 === 0 ? NW.white : NW.gray50;
                return (
                  <tr key={topIdx} style={{ background: rowBg }}>
                    {row.slice(0, rowDimCount).map((cell: any, ci: number) => (
                      <td
                        key={ci}
                        style={tdS({
                          background:
                            ci === 0
                              ? topIdx % 2 === 0
                                ? NW.violetSoft
                                : "#EEF4FB"
                              : rowBg,
                          fontWeight: ci === 0 ? 700 : 500,
                          color: ci === 0 ? NW.violetDark : NW.gray800,
                          borderRight:
                            ci === rowDimCount - 1
                              ? `3px solid ${NW.violet}`
                              : undefined,
                          position: ci === 0 ? "sticky" : undefined,
                          left: ci === 0 ? 0 : undefined,
                          zIndex: ci === 0 ? 2 : undefined,
                          boxShadow:
                            ci === 0
                              ? "2px 0 6px rgba(24,75,140,0.06)"
                              : undefined,
                        })}
                      >
                        {String(cell ?? "")}
                      </td>
                    ))}
                    {dataCols.map((_, colIdx) =>
                      filteredResults.map((r, ri) => {
                        // ✅ Chercher la ligne par valeur de dimension, pas par index
                        // → évite le décalage quand P1 et P2 ont des lignes dans des ordres différents
                        const dimKey = String(
                          row[rowDimCount > 0 ? rowDimCount - 1 : 0] ?? "",
                        );
                        const matchRow =
                          ri === 0
                            ? filteredResults[0].data.rows[origIdx]
                            : r.data.rows.find(
                                (rr: any[]) =>
                                  String(
                                    rr[rowDimCount > 0 ? rowDimCount - 1 : 0] ??
                                      "",
                                  ) === dimKey,
                              );
                        return (
                          <td
                            key={`${colIdx}-${ri}`}
                            style={tdS({
                              textAlign: "right",
                              color: NW.gray800,
                              fontVariantNumeric: "tabular-nums",
                            })}
                          >
                            {fmtNum(matchRow?.[rowDimCount + colIdx])}
                          </td>
                        );
                      }),
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── Dashboard view ────────────────────────────────────────────────────────
  const renderDashboard = () => {
    if (results.length === 0) return null;
    const rowDimCount =
      rows.length > 0
        ? Math.min(rows.length, results[0].data.columns.length)
        : 0;
    const rowLabelIdx = rowDimCount > 0 ? rowDimCount - 1 : 0;

    // ── Grouped comparison charts (toutes les périodes côte-à-côte) ──────────
    const renderComparisonCharts = () => {
      if (results.length < 2) return null;
      const columnMembers = results[0].data.columns.slice(rowDimCount);
      if (columnMembers.length === 0) return null;

      const tooltipStyle: React.CSSProperties = {
        borderRadius: 10,
        border: `1px solid #D7E5F5`,
        fontFamily: FONTS.body,
        fontSize: 12,
        boxShadow: "0 4px 20px rgba(24,75,140,0.15)",
      };

      // Chart type button definitions
      const chartTypeBtns: { type: string; icon: string; label: string }[] = [
        { type: "bar", icon: "📊", label: "Barres" },
        { type: "line", icon: "📈", label: "Ligne" },
        { type: "area", icon: "🏔️", label: "Aire" },
      ];

      return (
        <div
          style={card({
            padding: "18px 20px",
            marginTop: 4,
            borderColor: `${NW.violet}33`,
          })}
        >
          {/* Section header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 18,
              paddingBottom: 12,
              borderBottom: `1.5px solid ${NW.violetMed}`,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: NW.violet,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
                flexShrink: 0,
              }}
            >
              📊
            </div>
            <div>
              <div
                style={{
                  fontFamily: FONTS.heading,
                  fontWeight: 800,
                  fontSize: 13,
                  color: NW.violet,
                  letterSpacing: -0.2,
                }}
              >
                Comparaison côte-à-côte
              </div>
              <div style={{ fontSize: 11, color: NW.gray500, marginTop: 1 }}>
                Chaque groupe de valeurs regroupe toutes les périodes
              </div>
            </div>
            {/* Legend pills */}
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              {results.map((r, ri) => {
                const c = PERIOD_COLORS[ri % PERIOD_COLORS.length];
                return (
                  <div
                    key={ri}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      background: `${c}15`,
                      border: `1.5px solid ${c}55`,
                      borderRadius: 99,
                      padding: "3px 10px",
                      fontSize: 11,
                      fontWeight: 700,
                      color: c,
                      fontFamily: FONTS.body,
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: 10,
                        height: 10,
                        borderRadius: 3,
                        background: c,
                        flexShrink: 0,
                      }}
                    />
                    {r.period.label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* One grouped chart per column member */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(480px, 1fr))",
              gap: 16,
            }}
          >
            {columnMembers.map((member: string, memberIdx: number) => {
              // Collect all dim values across all periods (exclude "All")
              const allDimValues = new Set<string>();
              results.forEach((r) => {
                r.data.rows
                  .filter(
                    (row: unknown[]) =>
                      !isExcludedMember((row as any[])[0]) &&
                      !isExcludedMember((row as any[])[rowLabelIdx]),
                  )
                  .forEach((row: unknown[]) =>
                    allDimValues.add(String(row[rowLabelIdx] ?? "")),
                  );
              });

              // Build grouped data: [{name:'SH', 'Période 1': val, 'Période 2': val}, ...]
              const rawChartData = Array.from(allDimValues).map((dimVal) => {
                const entry: Record<string, any> = { name: dimVal };
                results.forEach((r) => {
                  const matchRow = r.data.rows.find(
                    (row: unknown[]) =>
                      String(row[rowLabelIdx] ?? "") === dimVal,
                  );
                  const raw = matchRow
                    ? -fmtVal(matchRow[rowDimCount + memberIdx])
                    : null;
                  entry[r.period.label] = raw;
                });
                return entry;
              });

              // ✅ Filter: exclude rows where ALL periods have null/0 values
              const chartData = rawChartData.filter((entry) =>
                results.some((r) => {
                  const v = entry[r.period.label];
                  return v != null && v !== 0;
                }),
              );

              // Sort by first period desc
              chartData.sort(
                (a, b) =>
                  ((b[results[0].period.label] as number) ?? 0) -
                  ((a[results[0].period.label] as number) ?? 0),
              );

              // ✅ Appliquer le Top N choisi
              const displayChartData = chartData.slice(0, effectiveTopN);

              // ✅ Ne pas afficher la carte si aucune donnée à visualiser
              if (displayChartData.length === 0) return null;

              const chartTypeKey = `${memberIdx}-${member}`;
              const activeType = compChartTypes[chartTypeKey] ?? "bar";

              const commonAxes = (
                <>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={NW.gray100}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    tick={{
                      fontSize: 10,
                      fill: NW.gray700,
                      fontFamily: FONTS.body,
                    }}
                    interval={0}
                    angle={-30}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    tick={{
                      fontSize: 10,
                      fill: NW.gray500,
                      fontFamily: FONTS.body,
                    }}
                    tickFormatter={(v) =>
                      Math.abs(v) >= 1000000
                        ? `${(v / 1000000).toFixed(1)}M`
                        : Math.abs(v) >= 1000
                          ? `${(v / 1000).toFixed(0)}k`
                          : String(v)
                    }
                  />
                  <Tooltip
                    formatter={(v: any) =>
                      typeof v === "number" ? v.toLocaleString("fr-FR") : v
                    }
                    contentStyle={tooltipStyle}
                  />
                  <Legend
                    wrapperStyle={{
                      fontFamily: FONTS.body,
                      fontSize: 11,
                    }}
                  />
                </>
              );

              return (
                <div
                  key={member}
                  style={card({
                    padding: "14px 16px",
                    borderColor: `${NW.violet}22`,
                  })}
                >
                  {/* Card header with chart type switcher */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginBottom: 12,
                    }}
                  >
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 3,
                        background: NW.violet,
                        display: "inline-block",
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontFamily: FONTS.heading,
                        fontWeight: 700,
                        fontSize: 13,
                        color: NW.violetDark,
                      }}
                    >
                      {member}
                    </span>
                    {/* Chart type switcher buttons */}
                    <div
                      style={{
                        marginLeft: "auto",
                        display: "flex",
                        gap: 4,
                        background: NW.gray100,
                        borderRadius: 10,
                        padding: 3,
                      }}
                    >
                      {chartTypeBtns.map(({ type, icon, label }) => {
                        const isActive = activeType === type;
                        return (
                          <button
                            key={type}
                            title={label}
                            onClick={() =>
                              setCompChartTypes((prev) => ({
                                ...prev,
                                [chartTypeKey]: type,
                              }))
                            }
                            style={{
                              width: 30,
                              height: 30,
                              border: "none",
                              borderRadius: 8,
                              cursor: "pointer",
                              fontSize: 14,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: isActive
                                ? NW.violet
                                : "transparent",
                              boxShadow: isActive
                                ? "0 2px 8px rgba(24,75,140,0.28)"
                                : "none",
                              transition: "all 0.15s",
                              transform: isActive ? "scale(1.1)" : "scale(1)",
                            }}
                          >
                            {icon}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <ResponsiveContainer width="100%" height={290}>
                    {activeType === "line" ? (
                      <LineChart data={displayChartData}>
                        {commonAxes}
                        {results.map((r, ri) => {
                          const lineColor =
                            PERIOD_COLORS[ri % PERIOD_COLORS.length];
                          return (
                            <Line
                              key={ri}
                              type="monotone"
                              dataKey={r.period.label}
                              stroke={lineColor}
                              strokeWidth={2.5}
                              dot={{ r: 3, fill: lineColor }}
                              activeDot={{ r: 5 }}
                              connectNulls={false}
                            />
                          );
                        })}
                      </LineChart>
                    ) : activeType === "area" ? (
                      <AreaChart data={displayChartData}>
                        {commonAxes}
                        {results.map((r, ri) => {
                          const areaColor =
                            PERIOD_COLORS[ri % PERIOD_COLORS.length];
                          return (
                            <Area
                              key={ri}
                              type="monotone"
                              dataKey={r.period.label}
                              stroke={areaColor}
                              fill={`${areaColor}30`}
                              strokeWidth={2.5}
                              dot={{ r: 2, fill: areaColor }}
                              connectNulls={false}
                            />
                          );
                        })}
                      </AreaChart>
                    ) : (
                      /* ── Bar chart (default) ── */
                      <BarChart
                        data={displayChartData}
                        barCategoryGap="30%"
                        barGap={2}
                      >
                        {commonAxes}
                        {results.map((r, ri) => {
                          const barColor =
                            PERIOD_COLORS[ri % PERIOD_COLORS.length];
                          return (
                            <Bar
                              key={ri}
                              dataKey={r.period.label}
                              fill={barColor}
                              radius={[5, 5, 0, 0]}
                              maxBarSize={40}
                            />
                          );
                        })}
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                </div>
              );
            })}
          </div>
        </div>
      );
    };

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* ── Individual period panels ── */}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 16,
            overflowX: "auto",
            alignItems: "flex-start",
          }}
        >
          {results.map((r, ri) => {
            const periodColor = PERIOD_COLORS[ri % PERIOD_COLORS.length];
            return (
              <div
                key={ri}
                style={card({
                  overflow: "hidden",
                  minWidth: 480,
                  flex: "1 1 480px",
                  borderColor: `${periodColor}44`,
                })}
              >
                {/* Period header */}
                <div
                  style={{
                    background: `linear-gradient(135deg, ${periodColor}, ${periodColor}CC)`,
                    color: "#FFFFFF",
                    padding: "12px 18px",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      background: "rgba(255,255,255,0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: FONTS.heading,
                      fontWeight: 800,
                      fontSize: 14,
                      border: "1px solid rgba(255,255,255,0.2)",
                    }}
                  >
                    P{ri + 1}
                  </div>
                  <div>
                    <div
                      style={{
                        fontFamily: FONTS.heading,
                        fontWeight: 800,
                        fontSize: 14,
                      }}
                    >
                      {r.period.label}
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.85 }}>
                      {r.period.dateFrom} → {r.period.dateTo}
                    </div>
                    {r.period.soldeCodePeriode && (
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          marginTop: 3,
                          background: "rgba(255,255,255,0.22)",
                          borderRadius: 4,
                          padding: "1px 7px",
                          display: "inline-block",
                        }}
                      >
                        🏷️ {r.period.soldeCodePeriode}
                        {r.period.demarquePhase
                          ? ` · ${r.period.demarquePhase}`
                          : ""}
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      marginLeft: "auto",
                      textAlign: "center",
                      background: "rgba(255,255,255,0.15)",
                      border: "1px solid rgba(255,255,255,0.2)",
                      borderRadius: 10,
                      padding: "6px 14px",
                    }}
                  >
                    <div style={{ fontSize: 18, fontWeight: 800 }}>
                      {
                        (r.data.rows ?? []).filter((row: any[]) => {
                          for (
                            let di = 0;
                            di < Math.max(rowDimCount, 1);
                            di++
                          ) {
                            if (isExcludedMember(row[di])) return false;
                          }
                          return true;
                        }).length
                      }
                    </div>
                    <div
                      style={{
                        fontSize: 9,
                        opacity: 0.7,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                      }}
                    >
                      Lignes
                    </div>
                  </div>
                </div>
                {/* Dashboard content */}
                <div style={{ padding: 14 }}>
                  <PeriodDashboard
                    data={r.data}
                    rowDimCount={rowDimCount}
                    accentColor={periodColor}
                    topN={isFinite(effectiveTopN) ? effectiveTopN : undefined}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Grouped comparison charts ── */}
        {renderComparisonCharts()}
      </div>
    );
  };

  const hasResults = results.length > 0;

  // ── Calcul des codes solde utilisés par les autres périodes ──────────────
  // Passé à chaque SoldeDropdown pour filtrer les choix déjà pris
  const getUsedSoldeCodesExcept = (currentIndex: number): string[] =>
    periods
      .filter((p, i) => i !== currentIndex && !!p.soldeCodePeriode)
      .map((p) => p.soldeCodePeriode as string);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 14,
        fontFamily: FONTS.body,
      }}
    >
      <style>{`
        @keyframes nwDateShake {
          0%{transform:translateX(0);} 15%{transform:translateX(-5px);} 30%{transform:translateX(5px);}
          45%{transform:translateX(-4px);} 60%{transform:translateX(4px);} 75%{transform:translateX(-2px);}
          90%{transform:translateX(2px);} 100%{transform:translateX(0);}
        }
        .nw-date-shake { animation: nwDateShake 0.35s ease; }
      `}</style>
      {/* ── Page header ── */}
      <div
        style={{
          background: `linear-gradient(135deg, ${NW.violetDark} 0%, ${NW.violet} 55%, #1D5BA8 100%)`,
          borderRadius: 16,
          padding: "16px 22px",
          display: "flex",
          alignItems: "center",
          gap: 14,
          boxShadow: "0 6px 32px rgba(24,75,140,0.28)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg, transparent, ${NW.roseMed}80, ${NW.roseMed}CC, ${NW.roseMed}80, transparent)`,
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: "rgba(255,255,255,0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 22,
            flexShrink: 0,
            boxShadow: "0 0 0 1px rgba(255,255,255,0.15)",
          }}
        >
          📅
        </div>
        <div>
          <div
            style={{
              fontFamily: FONTS.heading,
              fontWeight: 800,
              fontSize: 18,
              color: "#FFFFFF",
              letterSpacing: -0.4,
            }}
          >
            Comparaison de Périodes
          </div>
          <div
            style={{
              fontSize: 11,
              color: "rgba(255,255,255,0.5)",
              fontWeight: 600,
              letterSpacing: 2,
              textTransform: "uppercase",
              marginTop: 2,
            }}
          >
            Analyse · Multi-périodes
          </div>
        </div>
        {hasResults && (
          <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
            <div
              style={{
                textAlign: "center",
                background: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 12,
                padding: "8px 18px",
              }}
            >
              <div style={{ fontSize: 20, fontWeight: 800, color: "#FFFFFF" }}>
                {results.length}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "rgba(255,255,255,0.55)",
                  textTransform: "uppercase",
                  letterSpacing: 1.2,
                }}
              >
                Périodes
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Period definitions card ── */}
      <div style={card({ padding: "16px 20px" })}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 14,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontFamily: FONTS.heading,
              fontWeight: 700,
              fontSize: 11,
              color: NW.amber,
              textTransform: "uppercase",
              letterSpacing: 1.5,
            }}
          >
            📅 Périodes de comparaison
          </span>
          <button
            onClick={addPeriod}
            style={{
              fontSize: 12,
              padding: "5px 14px",
              border: `1.5px solid ${NW.amber}55`,
              borderRadius: 99,
              cursor: "pointer",
              background: NW.amberLight,
              color: NW.amber,
              fontWeight: 700,
              fontFamily: FONTS.body,
            }}
          >
            + Ajouter période
          </button>
          <span
            style={{
              fontSize: 11,
              color: NW.gray500,
              padding: "3px 10px",
              background: NW.gray50,
              border: `1px solid ${NW.gray200}`,
              borderRadius: 99,
            }}
          >
            {periods.length} période{periods.length > 1 ? "s" : ""}
          </span>
        </div>
        {periods.map((p, i) => (
          <PeriodRow
            key={i}
            index={i}
            period={p}
            usedSoldeCodes={getUsedSoldeCodesExcept(i)}
            onChange={(np) => updatePeriod(i, np)}
            onRemove={() => removePeriod(i)}
          />
        ))}
      </div>

      {/* ── Controls ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={runComparison}
          disabled={loading}
          style={{
            padding: "9px 22px",
            background: loading
              ? NW.gray200
              : NW.violet,
            color: loading ? NW.gray500 : "#FFFFFF",
            border: "none",
            borderRadius: 10,
            cursor: loading ? "default" : "pointer",
            fontWeight: 700,
            fontSize: 13,
            fontFamily: FONTS.body,
            boxShadow: loading ? "none" : `0 4px 16px rgba(24,75,140,0.28)`,
            transition: "all 0.15s",
            display: "flex",
            alignItems: "center",
            gap: 7,
          }}
        >
          {loading ? "⏳ Chargement…" : "▶ Comparer les périodes"}
        </button>

        {/* ── Bouton Enregistrer ── */}
        <button
          onClick={() => hasResults && setShowSaveModal(true)}
          disabled={!hasResults}
          title={
            hasResults
              ? "Enregistrer cette comparaison"
              : "Comparez d'abord les périodes"
          }
          style={{
            padding: "9px 18px",
            background: hasResults
              ? NW.violet
              : NW.gray200,
            color: hasResults ? "#FFFFFF" : NW.gray500,
            border: "none",
            borderRadius: 10,
            cursor: hasResults ? "pointer" : "not-allowed",
            fontWeight: 700,
            fontSize: 13,
            fontFamily: FONTS.body,
            boxShadow: hasResults ? `0 4px 16px rgba(24,75,140,0.28)` : "none",
            transition: "all 0.15s",
            display: "flex",
            alignItems: "center",
            gap: 7,
            opacity: hasResults ? 1 : 0.55,
          }}
        >
          💾 Enregistrer
        </button>

        {/* View mode toggle */}
        {hasResults && (
          <div
            style={{
              display: "flex",
              border: `1.5px solid ${NW.gray200}`,
              borderRadius: 10,
              overflow: "hidden",
              boxShadow: "0 1px 4px rgba(15,23,42,0.05)",
            }}
          >
            {(["table", "dashboard"] as ViewMode[]).map((m) => (
              <button
                key={m}
                onClick={() => setViewMode(m)}
                style={{
                  padding: "7px 16px",
                  fontSize: 13,
                  border: "none",
                  cursor: "pointer",
                  background:
                    viewMode === m
                      ? NW.violet
                      : NW.white,
                  color: viewMode === m ? "#FFFFFF" : NW.gray500,
                  fontWeight: viewMode === m ? 700 : 400,
                  fontFamily: FONTS.body,
                  transition: "all 0.15s",
                }}
              >
                {m === "table" ? "📋 Tableau" : "📊 Dashboard"}
              </button>
            ))}
          </div>
        )}

        {/* Warnings */}
        {selMeasures.length === 0 && (
          <div
            style={{
              fontSize: 12,
              color: NW.danger,
              padding: "5px 12px",
              background: NW.dangerLight,
              borderRadius: 99,
              border: `1px solid ${NW.danger}30`,
            }}
          >
            ⚠ Sélectionnez une mesure
          </div>
        )}
        {rows.length === 0 && columns.length === 0 && (
          <div
            style={{
              fontSize: 12,
              color: NW.danger,
              padding: "5px 12px",
              background: NW.dangerLight,
              borderRadius: 99,
              border: `1px solid ${NW.danger}30`,
            }}
          >
            ⚠ Sélectionnez une dimension
          </div>
        )}
      </div>

      {/* ── Error ── */}
      {error && (
        <div
          style={{
            color: NW.danger,
            fontSize: 13,
            padding: "10px 16px",
            background: NW.dangerLight,
            borderRadius: 10,
            border: `1px solid ${NW.danger}30`,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          ⚠ {error}
        </div>
      )}

      {/* ── Results ── */}
      {hasResults && (viewMode === "table" ? renderTable() : renderDashboard())}

      {/* ── Modal Enregistrer ── */}
      {showSaveModal && (
        <SaveQueryModal
          onClose={() => setShowSaveModal(false)}
          type={saveType}
          section="comparaison"
          overrideMeasures={selMeasures}
          overrideComparisonPeriods={periods
            .filter(
              (p) =>
                p.dateFrom &&
                p.dateTo &&
                (p.dateFrom <= p.dateTo ||
                  !!p.demarquePhase ||
                  !!p.soldeCodePeriode),
            )
            .map((p) => ({
              label: p.label,
              dateFrom: p.dateFrom,
              dateTo: p.dateTo,
              soldeCodePeriode: p.soldeCodePeriode ?? null,
              demarquePhase: p.demarquePhase ?? null,
            }))}
          overrideRowsDimensions={rows}
          overrideColumnsDimensions={columns}
        />
      )}
    </div>
  );
};

export default ComparisonView;
